// Notification payloads and the per-group preference model.
//
// The channel policy, stated once:
//
//   EMAIL is the baseline for every notification class. It always goes unless the
//   address is unsubscribed or the preference is off.
//   SMS is opt-in and reserved for TIME-SENSITIVE classes.
//   WEB PUSH is optional and best-effort — it never gates anything.
//
// One deliberate asymmetry, carried over: an event ANNOUNCEMENT sends both SMS
// and email to the same member, while the day-of ping falls back SMS-or-email. An
// announcement is the "group text" moment and delivery is aggressive on purpose;
// a ping only needs to arrive once.
export const NOTIFICATION_PREF_KEYS = [
    'notifyFeedPost',
    'notifyReactionActivity',
    'notifyEventReminder',
    'notifyMinReached',
    'notifyMaxReached',
    'notifyRsvpUpdates',
    // Last, because it applies to the fewest groups: only the ones that run
    // tournaments. A member of a book club should not meet it first.
    'notifyTournamentRound',
    // Last, and only meaningful to an owner or admin of a PUBLIC group on the
    // approval policy. Everyone else sees a switch that will never fire, which is
    // the honest trade for keeping one prefs screen rather than a role-dependent
    // one -- the same reason the tournament key sits above it.
    'notifyJoinRequest',
    // Near the top in the UI would be defensible -- it is the most frequent one
    // for a group that posts daily -- but it goes last with the other two
    // narrow ones so the order stays "what most groups meet first".
    'notifyMemberPost',
];
/**
 * The switches the prefs screens SHOW. `NOTIFICATION_PREF_KEYS` stays the full
 * list, because it is also what the server validates against.
 *
 * `notifyRsvpUpdates` is left out: no sender ever read it, so it was a switch
 * that did nothing either way. Hidden 2026-10-08 (docs/plan-notifications.md,
 * F3). Whether "RSVP changes" gets built is that doc's Q3.
 */
export const VISIBLE_NOTIFICATION_PREF_KEYS = NOTIFICATION_PREF_KEYS.filter((k) => k !== 'notifyRsvpUpdates');
/** Labels and help text for the prefs screen, so the copy lives in one place. */
export const NOTIFICATION_PREF_LABELS = {
    notifyFeedPost: {
        label: 'New events',
        help: 'When someone schedules an event in this group.',
    },
    notifyEventReminder: {
        label: 'Day-of check-in',
        help: 'The “still coming?” message on the day of an event you said yes to.',
    },
    notifyMinReached: {
        label: 'Minimum reached',
        help: 'When enough people have said yes for an event to go ahead.',
    },
    notifyMaxReached: {
        label: 'Event full',
        help: 'When an event reaches its maximum.',
    },
    notifyRsvpUpdates: {
        label: 'RSVP changes',
        help: 'When someone responds to an event you organize.',
    },
    notifyReactionActivity: {
        label: 'Reactions',
        help: 'When someone reacts to one of your posts in this group.',
    },
    notifyMemberPost: {
        label: 'New posts',
        // ⚠ Says "not events" out loud. The neighbouring switch is literally
        // called notify_feed_post in the database and governs event
        // announcements, so a member who turns this off and still gets event mail
        // would reasonably think it was broken.
        help: 'When someone posts to this group. Not event announcements, those are “New events”.',
    },
    notifyJoinRequest: {
        label: 'Join requests',
        // Says who it reaches, because a member who is not an admin cannot act on
        // one of these and would otherwise wonder why the switch does nothing.
        help: 'When someone asks to join this group. Owners and admins only.',
    },
    notifyTournamentRound: {
        label: 'Tournament tables',
        // Says PUSH out loud, because this class is push-only and a member who has
        // never allowed notifications would otherwise leave it on and wonder why
        // nothing arrives. The rest of the classes fall back to email; this one has
        // nowhere to fall back to -- deliberately, since "move to table 7" is a
        // message category the registered A2P campaign does not declare.
        help: 'A push telling you your table when a tournament round is drawn. Push only.',
    },
};
//# sourceMappingURL=notify.js.map